import _ from "lodash";
import { EventsDTO } from "../domain/models/dto/EventsDTO";
import {
    IEvents,
    EventsFiltersValues,
    EventsFlatCollection,
} from "../domain/models/Events";
import { EventsService } from "../domain/services/EventsService";
import { EventsMapper } from "../mappers/EventMapper";
import { CollectionResponse, RemoveResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";

export class EventsServiceImpl extends BaseServiceImpl implements EventsService {
    static _instance: EventsService;
    private eventsMapper = new EventsMapper();
    private readonly eventsEndpoint = "/api/v1/events";

    private constructor() {
        super();
    }

    static getInstance(): EventsService {
        if (!EventsServiceImpl._instance) {
            EventsServiceImpl._instance = new EventsServiceImpl();
        }
        return EventsServiceImpl._instance;
    }

    instantiate(entity: IEvents): IEvents {
        return this.eventsMapper.fromDTO(entity);
    }

    async getAll(
        currentPage: number,
        currentLimit: number,
        filtersDTO: EventsFiltersValues = {} as EventsFiltersValues
    ): Promise<CollectionResponse<IEvents>> {
        try {
            let url = `${this.eventsEndpoint}?limit=${currentLimit}&page=${currentPage}&getEntityTranslations=true`;
            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (key && !_.isNil(value) && !_.isEmpty(value)) {
                    url += `&${key}=${value}`;
                }
            });

            const result = await this.get<EventsFlatCollection>(url);

            return {
                ...result,
                rows: result.rows.map((dto) => this.eventsMapper.fromDTO(dto)),
            };
        } catch (err) {
            console.log("Error in EventsServiceImpl.getAllEvents");
            console.log(err);
            throw new Error(String(err));
        }
    }

    async createOrUpdate(dto: IEvents): Promise<IEvents> {
        try {
            const encodedDto = this.eventsMapper.toDTO(dto);
            if (dto.id) {
                return this.patch(`${this.eventsEndpoint}/${dto.id}`, encodedDto);
            }

            return this.post(this.eventsEndpoint, encodedDto);
        } catch (err) {
            console.log("Error in EventsServiceImpl.createOrUpdateEvents");
            console.log(err);
        }
    }

    async deleteById(id: string): Promise<RemoveResponse> {
        try {
            if (!id) return { status: false };
            return super.delete(`${this.eventsEndpoint}/${id}`);
        } catch (err) {
            console.log("Error in EventsServiceImpl.deleteById");
            console.log(err);
        }
    }

    async getById(id: string): Promise<IEvents | null> {
        try {
            if (!id) return null;

            const dto = await this.get<EventsDTO>(
                `${this.eventsEndpoint}/${id}?getEntityTranslations=true`
            );
            return this.eventsMapper.fromDTO(dto);
        } catch (err) {
            console.log("Error in EventsServiceImpl.getEventsById");
            console.log(err);
            return null;
        }
    }
}

export function eventsServiceFactory(): EventsService {
    return EventsServiceImpl.getInstance();
}
